// ============================================
// MaterialCache — shared `.mat` → Material loader + per-guid cache
// ============================================
//
// The ONE place a runtime Material is built from a `.mat` guid. Every renderer uses it — the editor viewport,
// the editor GameView, and the player — so material loading lives in a single spot instead of being
// re-implemented (and forgotten) per renderer. Each host differs ONLY in how it resolves a material:
//   - editor: read the `.mat` from the VFS + compile the shader live via slang-wasm (resolveMaterialShaderFull)
//   - player: read the precompiled `{data, wgsl, reflection}` shipped by the build (ExportMaterialsStep)
// …so the host supplies a `resolve(guid)` (+ a `resolveTexture(guid)` for its own texture handles); this class
// owns the build (loadMaterialAsset) + cache + invalidation.
//
// `get()` is intentionally the `GameViewRenderer.setMaterialResolver` shape: synchronous, returns the cached
// Material or null, and kicks off an async load on the first miss so a later frame has it.

import { type IRenderContext } from '../core/IRenderContext';
import { type TextureHandle } from '../types';
import { type Material } from './Material';
import { loadMaterialAsset, type MaterialAssetData } from './MaterialAsset';
import { type MaterialReflection } from './MaterialReflection';

/** A fully-resolved material: its `.mat` data + the compiled shader (WGSL) + reflection. */
export interface ResolvedMaterial {
  data: MaterialAssetData;
  /** Compiled WGSL for `data.shader` (editor: slang-wasm output; player: build output). */
  wgsl: string;
  /** Reflection for the uniform layout — REQUIRED for `.slang` (reflectWgsl can't read emitted-Slang WGSL). */
  reflection: MaterialReflection | null;
}

export interface MaterialCacheDeps {
  ctx: IRenderContext;
  /** Resolve a `.mat` guid → its data + compiled shader, or null if unresolved/uncompilable. */
  resolve: (guid: string) => Promise<ResolvedMaterial | null>;
  /** Resolve a texture guid → a GPU handle on `ctx` (host-specific texture cache). Optional. */
  resolveTexture?: (guid: string) => TextureHandle | null | undefined;
}

export class MaterialCache {
  private readonly materials = new Map<string, Material | null>();
  private readonly pending = new Set<string>();

  constructor(private readonly deps: MaterialCacheDeps) {}

  /**
   * Cached Material for `guid`, or null. On the first miss this kicks off an async load (returning null until
   * it's ready), so the next frame that requests the same guid gets the built Material.
   */
  get(guid: string): Material | null {
    const cached = this.materials.get(guid);
    if (cached !== undefined) return cached;
    if (!this.pending.has(guid)) {
      this.pending.add(guid);
      void this.load(guid);
    }
    return null;
  }

  private async load(guid: string): Promise<void> {
    try {
      const r = await this.deps.resolve(guid);
      if (!r || !r.data.shader) {
        this.materials.set(guid, null);
        return;
      }
      const mat = loadMaterialAsset(this.deps.ctx, r.data, {
        resolveShaderSource: (ref) => (ref === r.data.shader ? r.wgsl : null),
        reflection: r.reflection,
        resolveTexture: this.deps.resolveTexture
          ? (g) => this.deps.resolveTexture!(g) ?? undefined
          : undefined,
      });
      this.materials.set(guid, mat);
    } catch {
      this.materials.set(guid, null);
    } finally {
      this.pending.delete(guid);
    }
  }

  /** Drop + destroy the cached Material for `guid` (e.g. after a live edit/reimport) so it reloads on next get. */
  invalidate(guid: string): void {
    const m = this.materials.get(guid);
    if (m) m.destroy();
    this.materials.delete(guid);
    this.pending.delete(guid);
  }

  /** Drop + destroy every cached Material. */
  dispose(): void {
    for (const m of this.materials.values()) m?.destroy();
    this.materials.clear();
    this.pending.clear();
  }
}
